<template>
  <div class="page-table scrollable only-y">
    <div class="no-table-box page-header">
      <el-row class="hidden-sm-and-up pb-15">
        <h1>
          <v-svg class="voxo-aqua" sprite="world-hand" />
          Partner Accounts
        </h1>
      </el-row>
      <el-row class="hidden-xs-only pb-15">
        <el-col :span="12">
          <h1>
            <v-svg class="voxo-aqua" sprite="world-hand" />
            Partner Accounts
          </h1>
        </el-col>
        <el-col class="text-right" :span="12">
          <el-tag>
            <i class="el-icon-info" style="margin-right: 4px" />
            Total: {{ total }}
          </el-tag>
        </el-col>
      </el-row>

      <div class="flex pb-20">
        <div class="box">
          <el-input v-model="search" placeholder="Search" clearable />
        </div>
      </div>
      <div class="vxe-table-height card-base card-shadow--medium">
        <vxe-grid
          round
          border="inner"
          ref="xGrid"
          height="100%"
          auto-resize
          empty-text="No Data"
          show-overflow="tooltip"
          highlight-hover-row
          @cell-click="cellClicked"
          @cell-mouseenter="rowHover"
          @cell-mouseleave="rowLeave"
          :data="tableData"
          :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
          :loading="tableLoading"
          :columns="tableColumns"
        />
      </div>

      <edit-form
        ref="editForm"
        :currentRecord="currentRecord"
        :clearFields="clearFields"
        :editVisible="editVisible"
        :editProcessing="editProcessing"
        :omniaThemeOptions="omniaThemeOptions"
        @editOperation="editOperation"
        @cancelOperation="cancelOperation"
      />
    </div>
  </div>
</template>

<script>
import { debounce } from "lodash";
import { mapActions } from "vuex";
import feathersClient from "../../../feathers/feathers-client";
import EditForm from "./components/editForm.vue";

export default {
  name: "PartnersTable",

  components: {
    EditForm
  },

  data() {
    return {
      total: 0,
      search: "",
      visibleActionsId: "",
      tableLoading: false,
      tableData: [],
      allPartners: [],
      clearFields: false,
      editVisible: false,
      editProcessing: false,
      currentRecord: null,
      omniaThemeOptions: [],
      tableColumns: [
        {
          field: "sprite",
          title: "",
          width: 50,
          slots: {
            default: () => {
              return [
                <div class="voxo-square-icon voxo-bg-btn">
                  <v-svg class="voxo-aqua" sprite="world-hand" />
                </div>
              ];
            }
          }
        },
        {
          field: "name",
          title: "Name",
          minWidth: 150,
          sortable: true,
          showOverflow: true
        },
        {
          field: "type",
          title: "Type",
          minWidth: 75,
          sortable: true,
          showOverflow: false
        },
        {
          field: "actions",
          title: "",
          minWidth: "150",
          showOverflow: false,
          slots: {
            default: ({ row }) => {
              return [
                <div v-show={row.id === this.visibleActionsId}>
                  <el-tooltip placement="top" content="Edit">
                    <el-button
                      id={"edit-" + row.id}
                      icon="el-icon-edit"
                      onClick={() => this.openEditModal(row.id)}
                      class="table-action-button icon-blue"
                    />
                  </el-tooltip>
                </div>
              ];
            }
          }
        }
      ]
    };
  },

  computed: {
    user() {
      return this.$store.state.auth.user;
    }
  },

  watch: {
    user() {
      this.findPartnerAccounts();
    },

    search: debounce(function() {
      this.searchPartners();
    }, 500)
  },

  methods: {
    ...mapActions("partners", {
      patchPartner: "patch"
    }),

    openEditModal(id) {
      this.clearFields = false;
      feathersClient
        .service("partners")
        .get(id)
        .then(res => {
          this.currentRecord = { ...res };
          this.editVisible = true;
        });
    },

    editOperation(editBody) {
      this.$confirm("Update Partner Details?", "Warning", {
        confirmButtonText: "Update",
        cancelButtonText: "Cancel"
      })
        .then(() => {
          this.editProcessing = true;
          this.patchPartner([this.currentRecord.id, editBody])
            .then(res => {
              this.editProcessing = false;
              this.$notify({
                title: "Success",
                message: "Account " + res.name + " has been updated!",
                type: "success"
              });
              this.cancelOperation("edit");
            })
            .catch(error => {
              this.editProcessing = false;
              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error"
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error"
                });
              }
            });
        })
        .catch(() => true);
    },

    cancelOperation(type) {
      if (type === "edit") {
        this.editVisible = false;
      }
      this.clearFields = true;
    },

    searchPartners() {
      if (this.search.length) {
        this.tableData = this.allPartners.filter(
          data =>
            (data.name &&
              data.name.toLowerCase().includes(this.search.toLowerCase())) ||
            (data.type &&
              data.type.toLowerCase().includes(this.search.toLowerCase()))
        );
      } else {
        this.tableData = [...this.allPartners];
      }

      this.total = this.tableData.length;
    },

    findPartnerAccounts() {
      this.tableLoading = true;
      feathersClient
        .service("partners")
        .find()
        .then(res => {
          this.tableData = [...res];
          this.allPartners = [...res];
          this.total = res.length;
          this.tableLoading = false;
        });
    },

    //General Form and Table Operations
    cellClicked(data) {
      if (data.column.property !== "actions") {
        this.openEditModal(data.row.id);
      }
    },

    rowHover(data) {
      this.visibleActionsId = data.row.id;
    },

    rowLeave() {
      this.visibleActionsId = "";
    },

    removeListeners() {
      feathersClient.service("partners").removeListener("patched");
      feathersClient.service("partners").removeListener("created");
      feathersClient.service("partners").removeListener("removed");
    }
  },

  created() {
    if (this.user) {
      if (+this.user.userRole === 1) {
        this.findPartnerAccounts();
      }
    }

    feathersClient.service('omnia-themes').find({
      paginate: false
    }).then(res => {
      this.omniaThemeOptions = [...res];
    });

    //event listeners
    feathersClient.service("partners").on("patched", data => {
      //find the record in the table data.
      const updateIndex = this.tableData.findIndex(item => item.id === data.id);
      const updateAllIndex = this.allPartners.findIndex(
        item => item.id === data.id
      );
      if (updateIndex >= 0) {
        this.tableData.splice(updateIndex, 1, data);
      }
      if (updateAllIndex >= 0) {
        this.allPartners.splice(updateAllIndex, 1, data);
      }
    });

    feathersClient.service("partners").on("created", data => {
      if (+this.user.userRole === 1) {
        this.tableData.push(data);
        this.allPartners.push(data);
      }

      if (+this.user.userRole === 3 || +this.user.userRole === 12) {
        const { tenantIds } = this.user;
        if (tenantIds.includes(+data.id)) {
          this.tableData.push(data);
          this.allPartners.push(data);
        }
      }

      if (+this.user.userRole === 11) {
        const { partnerId: userPartnerId } = this.user;
        if (userPartnerId === data.partnerId) {
          this.tableData.push(data);
          this.allPartners.push(data);
        }
      }
    });

    feathersClient.service("partners").on("removed", data => {
      const indexToRemove = this.tableData.findIndex(
        item => item.id === data.id
      );
      const indexAllToRemove = this.allPartners.findIndex(
        item => item.id === data.id
      );
      if (indexToRemove >= 0) {
        this.tableData.splice(indexToRemove, 1);
      }
      if (indexAllToRemove >= 0) {
        this.allPartners.splice(indexAllToRemove, 1);
      }
    });
  },

  beforeDestroy() {
    this.$refs.xGrid.$destroy();
    this.$refs.editForm.$destroy();
    this.removeListeners();
  }
};
</script>
